import {
  BehaviorSubject,
  of,
  Subscription,
} from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ChipModule } from 'primeng/chip';
import { CheckboxModule } from 'primeng/checkbox';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { GridCellComponent } from '../grid-cell/grid-cell.component';
import {
  TranslateModule,
} from '@ngx-translate/core';
import { EmptyStateContainerComponent } from './empty-state/empty-state-container/empty-state-container.component';
import { SkeletonModule } from 'primeng/skeleton';
import { ScrollerModule as VirtualScrollerModule } from 'primeng/scroller';
import { ProgressBarModule } from 'primeng/progressbar';
import { GridData } from '@fitness-force/enum';
import { ScrollingModule } from '@angular/cdk/scrolling';
@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    SelectModule,
    ButtonModule,
    SelectButtonModule,
    MultiSelectModule,
    ChipModule,
    FormsModule,
    ReactiveFormsModule,
    CheckboxModule,
    TableModule,
    DialogModule,
    DatePickerModule,
    RippleModule,
    StyleClassModule,
    GridCellComponent,
    SkeletonModule,
    ProgressBarModule,
    VirtualScrollerModule,
    ScrollingModule,
    EmptyStateContainerComponent,
    TranslateModule,
  ]
})
export class DataGridComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() columns = new BehaviorSubject<[]>([]);
  @Input() columnData = new BehaviorSubject([]);
  @Input() EmptyState = new BehaviorSubject([]);
  @Input() totalRecords = new BehaviorSubject({
    totalPage: 10,
    totalItems: 10,
    currentPage: 0,
    reset: true,
  });
  @Input() currentPage = new BehaviorSubject(null);
  @Input() selectable = new BehaviorSubject(true);
  @Input() scrollHeight = 'flex';
  @Input() headerClasses = 'surface-100 font-bold border-none white-space-nowrap uppercase';
  @Input() tableClasses = 'p-0 shadow-2 my-3';
  @Input() groupMode = null;
  @Input() groupRows = null;
  @Input() paginator = true;
  @Input() loading = false;
  @Input() showHeader = true;
  @Input() EmptyStateClass = 'pt-8 pb-2 text-center surface-0';
  @Input() selectionMode = 'multiple';
  @Input() rowSelectableFunction: ((data: any) => boolean) | undefined;
  @Input() gridName :string = '';
  @Input() resizableColumns: boolean = false;
  @Input() columnResizeMode: 'fit' | 'expand' = 'expand';

  
  @Output() emitValue = new EventEmitter();
  @Output() emitSelectValue = new EventEmitter();
  @Output() onAction = new EventEmitter();
  @Output() loadNextData = new EventEmitter();

  selectedRows = new BehaviorSubject(new Map());
  isAllSelected = new BehaviorSubject(false);

  itemsPerPage = 10;
  newPage = 0;
  pageSize: number = GridData.DefaultPageSize;
  value: boolean | undefined;

  cols: any[] | undefined;
  dataloaded: any = new BehaviorSubject([]);
  selectedValue: string | undefined;

  showCalendar: boolean = false;
  selectedTaskCategory: { name: string } | undefined;

  calendarInput: Date | undefined;

  first = 0;
  subscriptions: Subscription[] | any = [];
  isInfinityScroll = new BehaviorSubject(false);
  selectedRowData:any;

  constructor(
    public changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this.columns);
    this.subscriptions.push(this.columnData);
    this.subscriptions.push(this.EmptyState);
    this.subscriptions.push(this.totalRecords);
    this.totalRecords.subscribe((value: any) => {
      if (value.reset) this.first = 0;
    });
  }
  ngAfterViewInit() {
   }

  setAsObserVable(data: any) {
    return of(data);
  }

  setCheckBoxValue(i: number, d: any) {
    if (!this.isRowSelectable(d)) {
      return;
    }
    return Object.assign(d, { index: i });
  }

  trackByFn(index: number, item: any) {
    return index; // or item.id
  }

  goToNextPage(e: any) {
    this.pageSize = e.rows;
    let pn = e.first ? e.first / this.pageSize + 1 : 1;
    this.loadNextData.emit({ ps: this.pageSize, pn });
  }

  isRowSelectable(data: any): boolean {
    if (this.rowSelectableFunction) {
      return this.rowSelectableFunction(data);
    }
    return true; // Default to allowing selection if no function provided
  }

  selectedRow(columndata: any, selection: boolean) {
    // Check if row is selectable
    if (!this.isRowSelectable(columndata.data)) {
      return;
    }

    if (selection) {
      const tempMap = this.selectedRows.value;
      tempMap.set(columndata.data.index, columndata.data);
      this.selectedRows.next(tempMap);
      
      // Check if all selectable rows are selected
      const selectableRowCount = this.columnData.value
        .filter((row: any) => this.isRowSelectable(row)).length;
      this.isAllSelected.next(tempMap.size >= selectableRowCount);
    } else {
      const tempMap = this.selectedRows.value;
      tempMap.delete(columndata.data.index);
      this.selectedRows.next(tempMap);
      this.isAllSelected.next(false);
    }
    
    this.emitValue.emit({ data: columndata, selection: selection });
  }

  SelectAllRow(e: any) {
    if (e.checked) {
      this.isAllSelected.next(true);
      const selectableRowsMap = new Map();
      this.columnData.value.forEach((row: any, index: number) => {
        if (this.isRowSelectable(row)) {
          selectableRowsMap.set(index, { ...row, index });
        }
      });
      this.selectedRows.next(selectableRowsMap);
    } else {
      this.isAllSelected.next(false);
      this.selectedRows.next(new Map());
    }
    this.emitSelectValue.emit(e);
  }

  onActionEvent(event: any) {
    this.onAction.emit(event);
  }

  // TESTING PENDING FOR WRAPPING in single event ..
  emptyAction(event: any) {
    this.onAction.emit(event);
  }
  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: any) => {
      if (subscription instanceof Subscription) subscription.unsubscribe();
    });
  }

  isEmptyAndNull(obj: any) {
    if (!obj) return true;
    else if (Object.keys(obj).length === 0) {
      return true;
    } else return false;
  }
}
